/**
 * BulkChangeQueueModal — move every selected item to one queue. Items with
 * no owner adopt the queue's default owner on the way (itemsStore.updateItem
 * applies that rule for every queue move in the app).
 */
import { useMemo, useState } from 'react';
import { Modal, Select, Field, Callout } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { itemsWord, runBulk, type BulkActionResult } from './index';

export interface BulkChangeQueueModalProps {
  open: boolean;
  selectedIds: string[];
  onClose: () => void;
  onComplete: (result: BulkActionResult) => void;
}

/** The select's value for "no queue". Not '' — that is the placeholder's value, and
    choosing an option whose value equals the current one fires no change. */
const GENERAL = '__general__';

export function BulkChangeQueueModal({ open, selectedIds, onClose, onComplete }: BulkChangeQueueModalProps) {
  const [queueId, setQueueId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);
  const items = useItemsStore((s) => s.items);
  const updateItem = useItemsStore((s) => s.updateItem);

  const queues = useMemo(
    () =>
      Array.from(orgUnits.values())
        .map((ou) => ({ id: ou.id, name: ou.displayName?.trim() || ou.name?.trim() || ou.id }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [orgUnits],
  );

  const count = selectedIds.length;
  const chosenName = queueId === GENERAL ? 'General' : queues.find((q) => q.id === queueId)?.name;
  const alreadyThere = useMemo(
    () =>
      selectedIds.filter((id) => {
        const item = items.find((i) => i.id === id);
        return (item?.queueId ?? GENERAL) === (queueId ?? '\u0000');
      }).length,
    [selectedIds, items, queueId],
  );

  const apply = async () => {
    if (queueId === null || busy) return;
    setBusy(true);
    try {
      const result = await runBulk(
        'Moved to queue',
        selectedIds,
        (id) => items.find((i) => i.id === id)?.title ?? id,
        async (id) => {
          const item = items.find((i) => i.id === id);
          if (!item) return { ok: false, reason: 'Item not found' };
          if ((item.queueId ?? GENERAL) === queueId) return { ok: true };
          return updateItem(id, { queueId: queueId === GENERAL ? null : queueId } as Parameters<typeof updateItem>[1]);
        },
        (done, total) => setProgress({ done, total }),
      );
      onComplete(result);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      dismissible={!busy}
      size="sm"
      title={`Move ${itemsWord(count)} to a queue`}
      description="The queue is the org unit that holds the item. An item with no owner takes on the queue's default owner."
      footer={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" variant="cta" size="sm" onClick={() => void apply()} disabled={busy || queueId === null}>
            {busy && progress ? `Moving… ${progress.done} of ${progress.total}` : `Move ${itemsWord(count)}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Queue">
          <Select
            size="sm"
            value={queueId ?? ''}
            onChange={(e) => setQueueId(e.target.value || null)}
            disabled={busy}
            autoFocus
          >
            {queueId === null && <option value="">Choose a queue…</option>}
            <option value={GENERAL}>General</option>
            {queues.map((q) => (
              <option key={q.id} value={q.id}>{q.name}</option>
            ))}
          </Select>
        </Field>
        {queueId !== null && chosenName && (
          <Callout tone="slate">
            You are about to move {itemsWord(count)} to{' '}
            <strong className="text-[var(--text-pri)]">{chosenName}</strong>.
            {alreadyThere > 0 && ` ${itemsWord(alreadyThere)} ${alreadyThere === 1 ? 'is' : 'are'} already there and ${alreadyThere === 1 ? 'stays' : 'stay'} as ${alreadyThere === 1 ? 'it is' : 'they are'}.`}
          </Callout>
        )}
      </div>
    </Modal>
  );
}
